import React, { useContext, useState } from 'react'
import Typography from '@mui/material/Typography'
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material'
import AnDateTimePicker from '../../../../../components/DateTime/DateTimePicker'
import S3DocumentUploadDisplay from '../../../../../components/images/S3DocumentUploadDisplay'
import API from '../../../../../services/API'
import { VehicleContext } from '../../SingleVehicle'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'
import moment from 'moment'
import { Close, Delete } from '@mui/icons-material'
import AttachmentIcon from '@mui/icons-material/Attachment'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material'

const AddNewInsurance = ({ onClose, existingInsurance }) => {
  const [provider, setProvider] = useState(existingInsurance?.provider ?? '')
  const [policyNo, setPolicyNo] = useState(existingInsurance?.policyNo ?? '')
  const [dateFrom, setDateFrom] = useState(existingInsurance?.dateFrom ?? null)
  const [dateTo, setDateTo] = useState(existingInsurance?.dateTo ?? null)
  const [cost, setCost] = useState(existingInsurance?.cost ?? '')
  const [files, setFiles] = useState([])
  const [existingDocumentsToDelete, setExistingDocumentsToDelete] = useState([])
  const [openConfirmDeleteDocument, setOpenConfirmDeleteDocument] =
    useState(null)

  const { loadVehicle, vehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const handleSubmit = async (e) => {
    e.preventDefault()
    let s3Keys = [
      ...(existingInsurance?.documents ?? []).filter(
        (x) => !existingDocumentsToDelete.includes(x),
      ),
    ]

    const pushToDb = async () => {
      const payload = {
        insurance: {
          dateFrom,
          dateTo,
          policyNo,
          provider,
          documents: s3Keys,
          cost: cost === '' ? undefined : parseFloat(cost),
        },
        ...(existingInsurance?._id
          ? { insuranceId: existingInsurance._id }
          : {}),
      }
      await API.put('/fleet/' + vehicle._id, payload).then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Insurance Added' })
          loadVehicle()
          onClose()
        } else {
          setToast({
            message: 'Unable to add insurance. Please try again later',
            severity: 'error',
          })
        }
      })
    }

    if (files?.length) {
      const uploadedKeys = await Promise.all(
        Array.from(files).map(async (file) => {
          const res = await API.post('/gets3uploadurl', {
            folder: 'vehicles/documents',
            extension: file.type.split('/')[1],
          })
          await API.put(res.data.url, file, {
            headers: { 'Content-Type': file.type },
          })
          return res.data.key
        }),
      )
      s3Keys = [...s3Keys, ...uploadedKeys]
    }

    await pushToDb()

    if (Array.from(existingDocumentsToDelete).length) {
      Array.from(existingDocumentsToDelete).forEach(async (file) => {
        await API.post('/deletes3object', {
          key: file,
        })
      })
    }
  }

  return (
    <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="title2">
            {existingInsurance ? 'Edit Insurance for' : 'Add Insurance to'}{' '}
            {vehicle.callSign}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          label="Provider/Underwriter"
          fullWidth
          required
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={policyNo}
          onChange={(e) => setPolicyNo(e.target.value)}
          label="Policy No"
          fullWidth
          required
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <AnDateTimePicker
          date={dateFrom}
          onChange={(newDate) => setDateFrom(newDate)}
          required
          label="Valid From"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <AnDateTimePicker
          date={dateTo}
          onChange={(newDate) => setDateTo(newDate)}
          required
          label="Valid Until"
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          type="number"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          label="Cost"
          autoComplete="off"
          fullWidth
          InputProps={{
            inputProps: { min: 0, step: 0.01 },
            startAdornment: (
              <InputAdornment position="start">
                {orgSettings?.currency ?? '£'}
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {existingInsurance?.documents?.length ? (
        <Grid item xs={12}>
          <Stack gap={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Existing Documents
            </Typography>
            {(existingInsurance.documents ?? [])
              .filter((x) => !existingDocumentsToDelete.includes(x))
              .map((x, i) => {
                const fileTitle = () => {
                  const fileType =
                    typeof x === 'string' ? x.split('.').slice(-1)[0] : null
                  if (['jpeg', 'jpg', 'png', 'gif'].includes(fileType))
                    return 'Image'
                  if (fileType === 'pdf') return 'PDF'
                  return 'File'
                }
                return (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                    key={i}
                  >
                    <Stack
                      direction="row"
                      gap={1}
                      justifyContent="space-between"
                      alignItems="center"
                      paddingX={1}
                      paddingY={0.5}
                    >
                      <Stack direction="row" gap={1} alignItems="center">
                        <AttachmentIcon fontSize="small" color="action" />
                        <Typography variant="body2">{fileTitle()}</Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={() => setOpenConfirmDeleteDocument(x)}
                      >
                        <Delete fontSize="small" color="anRed" />
                      </IconButton>
                    </Stack>
                  </Box>
                )
              })}
          </Stack>
          <Dialog
            open={!!openConfirmDeleteDocument}
            onClose={() => setOpenConfirmDeleteDocument(null)}
          >
            <DialogTitle sx={{ fontWeight: 800 }}>
              Delete this document?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                The file will be permanently removed when you save. This cannot
                be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setOpenConfirmDeleteDocument(null)}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  setExistingDocumentsToDelete((prev) =>
                    prev.includes(openConfirmDeleteDocument)
                      ? prev
                      : [...prev, openConfirmDeleteDocument],
                  )
                  setOpenConfirmDeleteDocument(null)
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      ) : (
        ''
      )}
      <Grid item xs={12}>
        <TextField
          type="file"
          defaultValue={files}
          inputProps={{
            accept: ['application/pdf', '.doc', '.docx', 'image/*'],
            multiple: true,
          }}
          onChange={(e) => setFiles(e.target.files)}
          fullWidth
          label="Attachments"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!moment(dateFrom).isValid() || !moment(dateTo).isValid()}
          >
            {existingInsurance ? 'Save Changes' : 'Add Insurance'}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default AddNewInsurance
